/**
 * All write-side mutations for Hyperliquid trading + on-chain flows.
 *
 * Every mutation here follows the same four-stage shape:
 *   1. onMutate   — optimistic cache patch (see `./optimistic-updates.ts`)
 *   2. mutationFn — sign & send via `ExchangeClient` (see `features/trade/lib/order-execution.ts`)
 *   3. onError    — roll back the optimistic patch
 *   4. onSettled  — invalidate relevant queries (see `./invalidation-helpers.ts`)
 *
 * Real-time confirmation of fills / cancels arrives separately via
 * `useHyperUserEvents` (WS). The polling that backs these queries is only a
 * 30s safety net — do not rely on it for UX freshness.
 *
 * Read-side companion: `./use-hyperliquid-account.ts`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { toast } from "sonner";
import { getHyperliquidErrorMessage } from "#/features/trade/lib/hyperliquid-error";
import {
	buildOrderType,
	type CancelOrderParams,
	type CancelTwapParams,
	ensureBuilderFeeApproved,
	ensureDexAbstraction,
	formatOrderFields,
	type PlaceOrderParams,
	resolveAssetMeta,
} from "#/features/trade/lib/order-execution";
import {
	normalizeOrderSize,
	normalizePerpOrderPrice,
} from "#/features/trade/lib/order-format";
import {
	computeMarketSlippagePrice,
	MARKET_CLOSE_SLIPPAGE_PCT,
} from "#/features/trade/lib/trade-constants";
import { analytics } from "#/lib/analytics";
import { createTransfer } from "#/lib/api/wallet";
import { toNumber } from "#/lib/formatters";
import { BUILDER_ADDRESS, BUILDER_ENABLED, BUILDER_FEE } from "./builder-fee";
import {
	invalidateAccountState,
	invalidateOpenOrders,
	invalidateWalletBalances,
	scheduleAccountStateRefetch,
} from "./invalidation-helpers";
import {
	buildPlaceholderOrder,
	extractRestingOid,
	insertOrderIntoCache,
	type PositionSnapshot,
	reduceAccountPositions,
	removeOrdersFromCache,
	replaceOrderOidInCache,
	restoreAccountPositions,
	restoreOrdersInCache,
	upsertOrdersFromWs,
	type WsOrderPatch,
} from "./optimistic-updates";
import { queryKeys } from "./query-keys";
import { useExchangeClient } from "./use-exchange-client";
import type { OpenOrder } from "./use-hyperliquid-account";
import { useWallet } from "./use-wallet";

export type {
	CancelOrderParams,
	CancelTwapParams,
	PlaceOrderParams,
} from "#/features/trade/lib/order-execution";

// HL "Account Type" code → analytics label.
const ACCOUNT_TYPE_LABELS = {
	i: "manual",
	u: "unified",
	p: "portfolio_margin",
} as const;

export function useDeposit() {
	const { address } = useWallet();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation({
		// The backend executes the on-chain USDC transfer to the HL bridge from
		// the server-custodied wallet (gas handled server-side) and confirms it
		// asynchronously — the WS sync / balance polling picks up the credit.
		// The tx hash isn't known at submit time (the deposit dialog's explorer
		// link only renders once a hash exists), hence the mapped return shape.
		mutationFn: async (amount: number): Promise<{ hash?: string }> => {
			if (!address) throw new Error("No wallet connected");
			const transfer = await createTransfer({
				kind: "hl_deposit",
				amount_usd: amount.toString(),
				idempotency_key: crypto.randomUUID(),
			});
			return { hash: transfer.tx_hash ?? undefined };
		},
		onSuccess: (_result, amount) => {
			// "Submitted", not "done" — the backend executes this
			// asynchronously and it can still fail. `TransferStatusSync`
			// polls for the terminal outcome and announces it; this
			// invalidation is what starts that polling.
			toast.success(t("toast.deposit-submitted"));
			void queryClient.invalidateQueries({
				queryKey: queryKeys.wallet.transfers(address),
			});
			analytics.track("deposit_submitted", { amount_usd: amount });
		},
		onError: (err, amount) => {
			analytics.track("deposit_failed", {
				amount_usd: amount,
				error: err instanceof Error ? err.message : String(err),
			});
			toast.error(getHyperliquidErrorMessage(t, err, t("deposit.failed")));
		},
	});
}

export function useTransfer() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation({
		mutationFn: async (params: { amount: number; toPerp: boolean }) => {
			if (!address) throw new Error("No wallet connected");
			const { exchangeClient } = await client.get();
			return exchangeClient.usdClassTransfer({
				amount: params.amount.toString(),
				toPerp: params.toPerp,
			});
		},
		onSuccess: (_result, params) => {
			invalidateAccountState(queryClient, address);
			toast.success(t("toast.move-funds-complete"));
			analytics.track("funds_transferred", {
				amount_usd: params.amount,
				direction: params.toPerp ? "spot_to_perp" : "perp_to_spot",
			});
		},
		onError: (err) => {
			const message = getHyperliquidErrorMessage(
				t,
				err,
				t("transferWallet.failed"),
			);
			toast.error(message);
		},
	});
}

export function useTransferToWallet() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation({
		mutationFn: async (amount: string) => {
			if (!address) throw new Error("No wallet connected");
			const { exchangeClient } = await client.get();
			return exchangeClient.withdraw3({
				destination: address as `0x${string}`,
				amount,
			});
		},
		onSuccess: () => {
			invalidateAccountState(queryClient, address);
			invalidateWalletBalances(queryClient, address);
			toast.success(t("toast.transfer-submitted"));
		},
		onError: (err) => {
			const message = getHyperliquidErrorMessage(
				t,
				err,
				t("transferWallet.failed"),
			);
			toast.error(message);
		},
	});
}

export function useExternalWithdraw() {
	const { address } = useWallet();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation({
		// Server-side USDC transfer from the custodied wallet to an external
		// address; confirmed asynchronously (poll `getTransfer` if a flow ever
		// needs the tx hash).
		mutationFn: async (params: {
			amount: string;
			destination: `0x${string}`;
		}) => {
			if (!address) throw new Error("No wallet connected");
			return createTransfer({
				kind: "external_withdraw",
				amount_usd: params.amount,
				to_address: params.destination,
				idempotency_key: crypto.randomUUID(),
			});
		},
		onSuccess: (_result, params) => {
			invalidateWalletBalances(queryClient, address);
			// BUG FIX: Also invalidate Hyperliquid account state so balances refresh immediately
			invalidateAccountState(queryClient, address);
			// Queued, not settled — `TransferStatusSync` reports the outcome.
			toast.success(t("toast.withdrawal-submitted"));
			void queryClient.invalidateQueries({
				queryKey: queryKeys.wallet.transfers(address),
			});
			analytics.track("withdraw_submitted", {
				amount_usd: Number(params.amount),
			});
		},
		onError: (err) => {
			const message = getHyperliquidErrorMessage(t, err, t("withdraw.failed"));
			toast.error(message);
		},
	});
}

export function useUpdateLeverage() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation({
		mutationFn: async (params: {
			coin: string;
			isCross: boolean;
			leverage: number;
		}) => {
			if (!address) throw new Error("No wallet connected");
			const [{ exchangeClient }, { asset }] = await Promise.all([
				client.get(),
				resolveAssetMeta(params.coin),
			]);
			return exchangeClient.updateLeverage({
				asset,
				isCross: params.isCross,
				leverage: params.leverage,
			});
		},
		onSuccess: (_result, params) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.hyper.account(address),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.trade.activeAssetData(address),
			});
			toast.success(t("toast.leverage-updated"));
			analytics.track("leverage_updated", {
				symbol: params.coin,
				leverage: params.leverage,
				margin_mode: params.isCross ? "cross" : "isolated",
			});
		},
		onError: (err) => {
			const message = getHyperliquidErrorMessage(
				t,
				err,
				t("toast.error-leverage-update"),
			);
			toast.error(message);
		},
	});
}

/**
 * Change the account abstraction mode (HL "Account Type"):
 * `"i"` = Manual (disabled) · `"u"` = Unified Account · `"p"` = Portfolio Margin.
 * Agent-signed (same model as `ensureDexAbstraction`), so no master-wallet popup.
 * Switching changes how collateral/margin works, so invalidate the full account
 * surface (abstraction, account, spot balances, xyz, per-coin capacity).
 */
export function useSetAccountAbstraction() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation({
		mutationFn: async (abstraction: "i" | "u" | "p") => {
			if (!address) throw new Error("No wallet connected");
			const { exchangeClient } = await client.get();
			return exchangeClient.agentSetAbstraction({ abstraction });
		},
		onSuccess: (_result, abstraction) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.hyper.userAbstraction(address),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.hyper.accountXyz(address),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.trade.activeAssetData(address),
			});
			invalidateAccountState(queryClient, address);
			toast.success(t("account-type.updated", "Account type updated"));
			analytics.track("account_type_changed", {
				account_type: ACCOUNT_TYPE_LABELS[abstraction],
			});
		},
		onError: (err) => {
			toast.error(
				getHyperliquidErrorMessage(
					t,
					err,
					t("account-type.error", "Failed to update account type"),
				),
			);
		},
	});
}

type PlaceOrderContext =
	| { kind: "twap" }
	| { kind: "market" }
	| { kind: "reduce"; positionSnapshot: PositionSnapshot }
	| { kind: "limit"; placeholder: OpenOrder };

export function usePlaceOrder() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation<unknown, Error, PlaceOrderParams, PlaceOrderContext>({
		mutationFn: async (params) => {
			if (!address) throw new Error("No wallet connected");

			const isTwap = params.orderType === "TWAP";
			const [{ exchangeClient }, { asset, szDecimals }] = await Promise.all([
				client.get(),
				resolveAssetMeta(params.coin),
			]);

			const isDexAsset = asset >= 100_000;
			const dexSetup = isDexAsset
				? ensureDexAbstraction(exchangeClient, address)
				: Promise.resolve();
			const formattedSize = normalizeOrderSize(params.size, szDecimals);

			if (isTwap) {
				await dexSetup;
				return exchangeClient.twapOrder({
					twap: {
						a: asset,
						b: params.side === "buy",
						s: formattedSize,
						r: params.reduceOnly,
						m: Number(params.twapDuration) || 30,
						t: true,
					},
				});
			}

			const { formattedPrice, formattedTriggerPrice } = formatOrderFields(
				params,
				szDecimals,
			);

			await dexSetup;
			await ensureBuilderFeeApproved(exchangeClient, address);
			return exchangeClient.order({
				orders: [
					{
						a: asset,
						b: params.side === "buy",
						p: formattedPrice,
						s: formattedSize,
						r: params.reduceOnly,
						t: buildOrderType({
							...params,
							price: formattedPrice,
							size: formattedSize,
							triggerPrice: formattedTriggerPrice,
						}),
					},
				],
				grouping: "na",
				builder: BUILDER_ENABLED
					? { b: BUILDER_ADDRESS, f: BUILDER_FEE }
					: undefined,
			});
		},
		onMutate: async (params) => {
			if (params.orderType === "TWAP") return { kind: "twap" };

			const isMarket = params.orderType === "Market order";
			if (isMarket && params.reduceOnly) {
				await queryClient.cancelQueries({
					queryKey: queryKeys.hyper.account(address),
				});
				const positionSnapshot = reduceAccountPositions(queryClient, address, [
					{ coin: params.coin, deltaAbs: toNumber(params.size) },
				]);
				return { kind: "reduce", positionSnapshot };
			}
			if (isMarket) return { kind: "market" };

			await queryClient.cancelQueries({
				queryKey: queryKeys.hyper.openOrders(address),
			});
			const placeholder = buildPlaceholderOrder(params);
			insertOrderIntoCache(queryClient, address, placeholder);
			return { kind: "limit", placeholder };
		},
		onSuccess: (result, params, context) => {
			if (context?.kind === "limit") {
				const realOid = extractRestingOid(result);
				if (realOid != null) {
					replaceOrderOidInCache(
						queryClient,
						address,
						params.coin,
						context.placeholder.oid,
						realOid,
					);
				} else {
					removeOrdersFromCache(queryClient, address, [
						{ coin: params.coin, oid: context.placeholder.oid },
					]);
				}
			}
			toast.success(t("toast.order-placed"));
			analytics.track("order_submitted", {
				symbol: params.coin,
				side: params.side,
				order_type: params.orderType,
				size: toNumber(params.size),
				reduce_only: params.reduceOnly,
			});
		},
		onError: (err, params, context) => {
			if (context?.kind === "limit") {
				removeOrdersFromCache(queryClient, address, [
					{ coin: params.coin, oid: context.placeholder.oid },
				]);
			} else if (context?.kind === "reduce") {
				restoreAccountPositions(queryClient, address, context.positionSnapshot);
			}
			analytics.track("order_failed", {
				symbol: params.coin,
				side: params.side,
				order_type: params.orderType,
				error: err.message,
			});
			toast.error(
				getHyperliquidErrorMessage(t, err, t("toast.error-order-failed")),
			);
		},
		onSettled: (_result, _err, params) => {
			if (params.orderType === "TWAP") {
				queryClient.invalidateQueries({
					queryKey: queryKeys.hyper.twapHistory(address),
				});
			}
			// Position/margin refresh is delayed (see scheduleAccountStateRefetch):
			// the WS sync updates the position instantly, while an immediate REST
			// refetch right at fill time reads HL's stale post-fill snapshot and
			// clobbers the fresh WS value (position "lags by one fill").
			scheduleAccountStateRefetch(queryClient, address);
			queryClient.invalidateQueries({
				queryKey: queryKeys.trade.activeAssetData(address),
			});
			invalidateOpenOrders(queryClient, address);
		},
	});
}

type CancelOrderContext = { removed: OpenOrder[] };

export function useCancelOrder() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation<unknown, Error, CancelOrderParams, CancelOrderContext>({
		mutationFn: async (params) => {
			if (!address) throw new Error("No wallet connected");
			const [{ exchangeClient }, { asset }] = await Promise.all([
				client.get(),
				resolveAssetMeta(params.coin),
			]);
			return exchangeClient.cancel({ cancels: [{ a: asset, o: params.oid }] });
		},
		onMutate: async (params) => {
			await queryClient.cancelQueries({
				queryKey: queryKeys.hyper.openOrders(address),
			});
			const removed = removeOrdersFromCache(queryClient, address, [params]);
			return { removed };
		},
		onSuccess: (_result, params) => {
			toast.success(t("toast.order-cancelled"));
			analytics.track("order_canceled", { symbol: params.coin });
		},
		onError: (err, _params, context) => {
			if (context) restoreOrdersInCache(queryClient, address, context.removed);
			toast.error(
				getHyperliquidErrorMessage(t, err, t("toast.error-cancel-order")),
			);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.hyper.account(address),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.trade.activeAssetData(address),
			});
			invalidateOpenOrders(queryClient, address);
		},
	});
}

export type ModifyOrderParams = {
	order: OpenOrder;
	/** New limit price (ignored for trigger-market orders — p follows the trigger). */
	price: string;
	size: string;
	/** New trigger price; only read when `order.isTrigger`. */
	triggerPrice: string;
};

type ModifyOrderContext = { previous: OpenOrder | undefined };

/** Trigger-market orders are placed with p = trigger price (see handleSetTpsl /
 * the order form), so an edit moves the cap price along with the trigger. */
function isMarketTriggerOrder(order: OpenOrder): boolean {
	return order.isTrigger && order.orderType.endsWith("Market");
}

/**
 * Modify a resting order in place — atomic cancel+replace on HL, one
 * `batchModify` action (unlike `modify` it returns the resting oid).
 *
 * `a` (always_place) policy:
 *   - Limit orders omit it, so HL rejects the whole modify if the original
 *     just filled (no duplicate order) — the trade-off is that edits to an
 *     immediately-executable price are also rejected (error toast, original
 *     order survives). Non-executable Gtc edits get their tif overridden to
 *     Alo server-side; resting behavior is identical.
 *   - Trigger orders must send `a: true` — HL's default mode only accepts
 *     non-trigger replacements. Never send `a: false`; HL rejects that hash.
 */
export function useModifyOrder() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation<unknown, Error, ModifyOrderParams, ModifyOrderContext>({
		mutationFn: async ({ order, price, size, triggerPrice }) => {
			if (!address) throw new Error("No wallet connected");
			const [{ exchangeClient }, { asset, szDecimals }] = await Promise.all([
				client.get(),
				resolveAssetMeta(order.coin),
			]);

			const orderTypeWire = order.isTrigger
				? {
						trigger: {
							isMarket: isMarketTriggerOrder(order),
							triggerPx: normalizePerpOrderPrice(triggerPrice, szDecimals),
							tpsl: (order.orderType.startsWith("Stop") ? "sl" : "tp") as
								| "sl"
								| "tp",
						},
					}
				: {
						limit: {
							tif: (order.tif === "Alo" ? "Alo" : "Gtc") as "Alo" | "Gtc",
						},
					};

			return exchangeClient.batchModify({
				modifies: [
					{
						oid: order.oid,
						order: {
							a: asset,
							b: order.side === "B",
							p: normalizePerpOrderPrice(
								isMarketTriggerOrder(order) ? triggerPrice : price,
								szDecimals,
							),
							s: normalizeOrderSize(size, szDecimals),
							r: order.reduceOnly,
							t: orderTypeWire,
						},
					},
				],
				...(order.isTrigger ? { a: true as const } : {}),
			});
		},
		onMutate: async ({ order, price, size, triggerPrice }) => {
			await queryClient.cancelQueries({
				queryKey: queryKeys.hyper.openOrders(address),
			});
			const previous = queryClient
				.getQueryData<OpenOrder[]>(queryKeys.hyper.openOrders(address))
				?.find((o) => o.coin === order.coin && o.oid === order.oid);
			upsertOrdersFromWs(queryClient, address, [
				{
					coin: order.coin,
					side: order.side,
					limitPx: isMarketTriggerOrder(order) ? triggerPrice : price,
					sz: size,
					origSz: size,
					oid: order.oid,
					timestamp: Date.now(),
					reduceOnly: order.reduceOnly,
					triggerPx: order.isTrigger ? triggerPrice : undefined,
				},
			]);
			return { previous };
		},
		onSuccess: (result, { order }) => {
			// HL may re-issue the oid on modify; swap it so WS events keep matching.
			const newOid = extractRestingOid(result);
			if (newOid != null && newOid !== order.oid) {
				replaceOrderOidInCache(
					queryClient,
					address,
					order.coin,
					order.oid,
					newOid,
				);
			}
			toast.success(t("toast.order-modified"));
			analytics.track("order_modified", { symbol: order.coin });
		},
		onError: (err, _params, context) => {
			const prev = context?.previous;
			if (prev) {
				const rollback: WsOrderPatch = {
					coin: prev.coin,
					side: prev.side,
					limitPx: prev.limitPx,
					sz: prev.sz,
					origSz: prev.origSz,
					oid: prev.oid,
					timestamp: prev.timestamp,
					reduceOnly: prev.reduceOnly,
					triggerPx: prev.triggerPx,
				};
				upsertOrdersFromWs(queryClient, address, [rollback]);
			}
			toast.error(
				getHyperliquidErrorMessage(t, err, t("toast.error-modify-order")),
			);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.hyper.account(address),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.trade.activeAssetData(address),
			});
			invalidateOpenOrders(queryClient, address);
		},
	});
}

export type BulkClosePosition = {
	coin: string;
	/** absolute position size (no leading minus) */
	size: string;
	isLong: boolean;
	markPrice: number;
};

type CloseAllContext = { positionSnapshot: PositionSnapshot };

export function useCloseAllPositions() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation<unknown, Error, BulkClosePosition[], CloseAllContext>({
		mutationFn: async (positions) => {
			if (!address) throw new Error("No wallet connected");
			if (positions.length === 0) return null;

			const [{ exchangeClient }, metas] = await Promise.all([
				client.get(),
				Promise.all(positions.map((p) => resolveAssetMeta(p.coin))),
			]);

			const orders = positions.map((p, i) => {
				const { asset, szDecimals } = metas[i];
				return {
					a: asset,
					b: !p.isLong,
					p: computeMarketSlippagePrice(
						p.markPrice,
						p.isLong,
						MARKET_CLOSE_SLIPPAGE_PCT,
					),
					s: normalizeOrderSize(p.size, szDecimals),
					r: true,
					t: { limit: { tif: "FrontendMarket" as const } },
				};
			});

			await ensureBuilderFeeApproved(exchangeClient, address);
			return exchangeClient.order({
				orders,
				grouping: "na",
				builder: BUILDER_ENABLED
					? { b: BUILDER_ADDRESS, f: BUILDER_FEE }
					: undefined,
			});
		},
		onMutate: async (positions) => {
			if (positions.length === 0) return { positionSnapshot: new Map() };
			await queryClient.cancelQueries({
				queryKey: queryKeys.hyper.account(address),
			});
			const positionSnapshot = reduceAccountPositions(
				queryClient,
				address,
				positions.map((p) => ({
					coin: p.coin,
					deltaAbs: toNumber(p.size),
				})),
			);
			return { positionSnapshot };
		},
		onSuccess: (result, positions) => {
			if (result) {
				toast.success(t("toast.order-placed"));
				analytics.track("position_closed_all", { count: positions.length });
			}
		},
		onError: (err, _positions, context) => {
			if (context) {
				restoreAccountPositions(queryClient, address, context.positionSnapshot);
			}
			toast.error(
				getHyperliquidErrorMessage(t, err, t("toast.error-order-failed")),
			);
		},
		onSettled: () => {
			invalidateAccountState(queryClient, address);
			queryClient.invalidateQueries({
				queryKey: queryKeys.trade.activeAssetData(address),
			});
			invalidateOpenOrders(queryClient, address);
		},
	});
}

export function useCancelAllOrders() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation<unknown, Error, CancelOrderParams[], CancelOrderContext>({
		mutationFn: async (orders) => {
			if (!address) throw new Error("No wallet connected");
			if (orders.length === 0) return null;
			const [{ exchangeClient }, metas] = await Promise.all([
				client.get(),
				Promise.all(orders.map((o) => resolveAssetMeta(o.coin))),
			]);
			return exchangeClient.cancel({
				cancels: orders.map((o, i) => ({ a: metas[i].asset, o: o.oid })),
			});
		},
		onMutate: async (orders) => {
			if (orders.length === 0) return { removed: [] };
			await queryClient.cancelQueries({
				queryKey: queryKeys.hyper.openOrders(address),
			});
			const removed = removeOrdersFromCache(queryClient, address, orders);
			return { removed };
		},
		onSuccess: (result, orders) => {
			if (!result) return;
			toast.success(t("toast.order-cancelled"));
			analytics.track("orders_canceled_all", { count: orders.length });
		},
		onError: (err, _orders, context) => {
			if (context) restoreOrdersInCache(queryClient, address, context.removed);
			toast.error(
				getHyperliquidErrorMessage(t, err, t("toast.error-cancel-order")),
			);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.hyper.account(address),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.trade.activeAssetData(address),
			});
			invalidateOpenOrders(queryClient, address);
		},
	});
}

type CancelTwapContext = { previous: unknown[] | undefined };

export function useCancelTwap() {
	const { address } = useWallet();
	const client = useExchangeClient();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	return useMutation<unknown, Error, CancelTwapParams, CancelTwapContext>({
		mutationFn: async (params) => {
			if (!address) throw new Error("No wallet connected");
			const [{ exchangeClient }, { asset }] = await Promise.all([
				client.get(),
				resolveAssetMeta(params.coin),
			]);
			return exchangeClient.twapCancel({ a: asset, t: params.twapId });
		},
		onMutate: async (params) => {
			const key = queryKeys.hyper.twapHistory(address);
			await queryClient.cancelQueries({ queryKey: key });
			const previous = queryClient.getQueryData<unknown[]>(key);
			queryClient.setQueryData<unknown[]>(key, (old) =>
				old?.map((item) => {
					const entry = item as { twapId?: number };
					return entry.twapId === params.twapId
						? { ...(item as object), status: { status: "terminated" } }
						: item;
				}),
			);
			return { previous };
		},
		onSuccess: () => {
			toast.success(t("toast.order-cancelled"));
		},
		onError: (err, _params, context) => {
			if (context?.previous !== undefined) {
				queryClient.setQueryData(
					queryKeys.hyper.twapHistory(address),
					context.previous,
				);
			}
			toast.error(
				getHyperliquidErrorMessage(t, err, t("toast.error-cancel-twap")),
			);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.hyper.twapHistory(address),
			});
		},
	});
}
